
import chartWideAt2xl from '@/components/charts/_shared/chartWideAt2xl.module.css';
import {
  STATE_CHART_ASPECT_RATIO,
  STATE_CHART_ASPECT_RATIO_MOBILE,
  STATE_CHART_MAX_WIDTH_REM,
} from '@/constants/stateCharts';

import styles from './StateDonutChartLayout.module.css';

import type { CSSProperties, ReactNode } from 'react';

type RootProps = {
  'aria-label': string;
  children: ReactNode;
  className?: string;
};

/**
 * Shared two-column shell for State donut charts (Population Trends +
 * Red List Categories). Sets `--donut-max-width` / `--donut-aspect-ratio`
 * (+ mobile square) for ChartResponsiveShell, plate min-height, and legend.
 */
function Root({ 'aria-label': ariaLabel, children, className }: RootProps) {
  const style = {
    ['--donut-max-width' as string]: `${STATE_CHART_MAX_WIDTH_REM}rem`,
    ['--donut-aspect-ratio' as string]: String(STATE_CHART_ASPECT_RATIO),
    ['--donut-aspect-ratio-mobile' as string]: String(
      STATE_CHART_ASPECT_RATIO_MOBILE,
    ),
  } as CSSProperties;

  return (
    <section
      className={[styles.root, chartWideAt2xl.wide, className]
        .filter(Boolean)
        .join(' ')}
      style={style}
      aria-label={ariaLabel}
    >
      {children}
    </section>
  );
}

function Decoration({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={className ? `${styles.decoration} ${className}` : styles.decoration}
      aria-hidden
    >
      {children}
    </div>
  );
}

function ChartColumn({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={
        className ? `${styles.chartColumn} ${className}` : styles.chartColumn
      }
    >
      {children}
    </div>
  );
}

function ChartPlate({ children }: { children: ReactNode }) {
  return <div className={styles.chartPlate}>{children}</div>;
}

function Legend({ children }: { children: ReactNode }) {
  return <div className={styles.legend}>{children}</div>;
}

export const StateDonutChartLayout = {
  Root,
  Decoration,
  ChartColumn,
  ChartPlate,
  Legend,
} as const;
